import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Clock, HelpCircle, BookOpen, ClipboardList, Beaker, Users, Folder, FileText, MessageSquare, Edit3, Sparkles, ArrowLeft, Loader2, CheckCircle, Download, Lightbulb } from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../AuthContext';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import './Assessments.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import configData from '../../configure';
import { uploadFileToCampusFileSystem } from '../../services/CampusFileSystemService';

export default function FacultyAssistant({ orgId: orgIdFromProps }) {
  const { axios, user, isLoading: authLoading, orgId: contextOrgId } = useAuth();
  const orgId = orgIdFromProps || contextOrgId || user?.orgId || '';
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showGeneratedContent, setShowGeneratedContent] = useState(false);
  const [contentHistory, setContentHistory] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [refinementInstructions, setRefinementInstructions] = useState('');
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activityMetrics, setActivityMetrics] = useState({
    generated: 0,
    published: 0,
    shared: 0,
  });
  const [showFileNameModal, setShowFileNameModal] = useState(false);
  const [fileNameInput, setFileNameInput] = useState('');
  const [publishRemarks, setPublishRemarks] = useState('');   // ← NEW
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(false);
  const [isJsPDFLoaded, setIsJsPDFLoaded] = useState(!!window.jspdf);
  const [jsPDFLoadError, setJsPDFLoadError] = useState(null);
  const [isHtml2CanvasLoaded, setIsHtml2CanvasLoaded] = useState(!!window.html2canvas);
  const [html2CanvasLoadError, setHtml2CanvasLoadError] = useState(null);
  const [showChatHistoryModal, setShowChatHistoryModal] = useState(false);

  // Dynamically load jsPDF
  useEffect(() => {
    if (!window.jspdf) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
      script.async = true;
      script.onload = () => setIsJsPDFLoaded(true);
      script.onerror = () =>
        setJsPDFLoadError('Failed to load jsPDF library. Please check your network or CDN availability.');
      document.body.appendChild(script);
      return () => document.body.removeChild(script);
    }
  }, []);

  // Dynamically load html2canvas
  useEffect(() => {
    if (!window.html2canvas) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
      script.async = true;
      script.onload = () => setIsHtml2CanvasLoaded(true);
      script.onerror = () =>
        setHtml2CanvasLoadError('Failed to load html2canvas library. Falling back to basic PDF generation.');
      document.body.appendChild(script);
      return () => document.body.removeChild(script);
    }
  }, []);

  // Utility function to clean Markdown heading hashes for DOCX and PDF
  const cleanMarkdownHeadings = (content) => {
    if (!content) return content;
    return content.replace(/^(#+)\s*/gm, '');
  };

  // Utility function to render Markdown to HTML
  const renderMarkdown = (content) => {
    if (!content) return '';
    const rawHtml = marked(content, { breaks: true });
    return DOMPurify.sanitize(rawHtml);
  };

  const taskCategories = useMemo(() => [
    {
      id: 'create-assignment',
      name: 'Create Assignment',
      icon: BookOpen,
      color: 'task-blue',
      prompts: {
        base: "Generate an assignment on the topic '{topic}'.",
        detailed: "Include questions of varying difficulty (easy, medium, hard) and types (multiple choice, short answer, problem-solving) with clear instructions and learning objectives."
      }
    },
    {
      id: 'create-case-study',
      name: 'Create Case Study',
      icon: ClipboardList,
      color: 'task-green',
      prompts: {
        base: "Design a case study on '{topic}'.",
        detailed: "Create a real-world problem scenario with guided questions, incorporating practical applications and critical thinking exercises."
      }
    },
    {
      id: 'create-mini-project',
      name: 'Create Mini Project',
      icon: Beaker,
      color: 'task-purple',
      prompts: {
        base: "Suggest a mini project on '{topic}'.",
        detailed: "Include project scope, objectives, step-by-step instructions, materials needed, and expected deliverables."
      }
    },
    {
      id: 'create-lab-exercise',
      name: 'Create Lab Exercise',
      icon: Users,
      color: 'task-orange',
      prompts: {
        base: "Design a lab exercise on '{topic}'.",
        detailed: "Provide step-by-step practical instructions, materials list, safety guidelines, and expected outcomes."
      }
    },
    {
      id: 'create-lecture-notes',
      name: 'Create Lecture Notes',
      icon: FileText,
      color: 'task-teal',
      prompts: {
        base: "Generate lecture notes on '{topic}'.",
        detailed: "Provide concise, structured notes with key concepts, diagrams, examples, and summaries for classroom use."
      }
    },
    {
      id: 'generate-video-urls',
      name: 'Generate Video URLs',
      icon: Folder,
      color: 'task-blue',
      prompts: {
        base: "Generate a list of video URLs for educational content on '{topic}'.",
        detailed: "Include relevant videos with timestamps, descriptions, and links suitable for classroom or online lessons."
      }
    },
    {
      id: 'curate-reference-material',
      name: 'Curate Reference Material',
      icon: BookOpen,
      color: 'task-gray',
      prompts: {
        base: "Curate reference material on '{topic}'.",
        detailed: "Suggest links to books, articles, videos, and papers relevant to the topic, ensuring age-appropriate and credible sources."
      }
    },
    {
      id: 'generate-quiz',
      name: 'Generate Quiz / MCQs',
      icon: ClipboardList,
      color: 'task-green',
      prompts: {
        base: "Create a 10-question quiz on '{topic}'.",
        detailed: "Include multiple-choice questions, true/false, and fill-in-the-blank questions with answers and explanations."
      }
    },
    {
      id: 'generate-flashcards',
      name: 'Generate Flashcards',
      icon: MessageSquare,
      color: 'task-teal',
      prompts: {
        base: "Generate flashcards on '{topic}'.",
        detailed: "Provide short question-and-answer pairs or keyword-based revision cards for quick review and memorization."
      }
    },
    {
      id: 'create-coding-problems',
      name: 'Create Coding Problems',
      icon: MessageSquare,
      color: 'task-purple',
      prompts: {
        base: "Create a simple Python simulation task on '{topic}'.",
        detailed: "Include input/output specifications, constraints, and a sample solution for a coding task simulating a scientific concept."
      }
    },
    {
      id: 'create-discussion-prompts',
      name: 'Create Discussion Prompts',
      icon: Users,
      color: 'task-orange',
      prompts: {
        base: "Generate discussion prompts on '{topic}'.",
        detailed: "Provide open-ended questions to encourage class discussions or debates, fostering critical thinking and engagement."
      }
    },
    {
      id: 'explain-concept',
      name: 'Explain Concept',
      icon: Lightbulb,
      color: 'task-yellow',
      prompts: {
        base: "Explain a key concept from Science Grade 9 on '{topic}'.",
        detailed: "Break down the concept into simple, digestible terms with examples and analogies suitable for Grade 9 students."
      }
    },
    {
      id: 'generate-analogies',
      name: 'Generate Analogies',
      icon: Sparkles,
      color: 'task-pink',
      prompts: {
        base: "Provide an analogy for a concept from Science Grade 9 on '{topic}'.",
        detailed: "Create a relatable analogy to help Grade 9 students understand the concept, ensuring clarity and engagement."
      }
    },
    {
      id: 'suggest-teaching-aids',
      name: 'Suggest Teaching Aids',
      icon: BookOpen,
      color: 'task-blue',
      prompts: {
        base: "Suggest teaching aids on '{topic}'.",
        detailed: "Recommend visuals, demonstrations, models, or digital tools to enhance teaching and student understanding."
      }
    },
    {
      id: 'summarize-research-papers',
      name: 'Summarize Research Papers',
      icon: FileText,
      color: 'task-gray',
      prompts: {
        base: "Summarize a basic article or paper on '{topic}' for Science Grade 9.",
        detailed: "Provide a simplified summary of key findings, concepts, and relevance, tailored for Grade 9 comprehension."
      }
    },
    {
      id: 'generate-differentiated-content',
      name: 'Generate Differentiated Content',
      icon: Users,
      color: 'task-orange',
      prompts: {
        base: "Create differentiated content on '{topic}'.",
        detailed: "Provide basic, intermediate, and advanced versions of content, including activities and assessments tailored to different learning levels."
      }
    },
    {
      id: 'design-rubrics',
      name: 'Design Rubrics',
      icon: ClipboardList,
      color: 'task-green',
      prompts: {
        base: "Create a rubric for evaluating assignments or projects on '{topic}' for Science Grade 9.",
        detailed: "Include clear evaluation criteria, scoring levels, and descriptors for assessing student work."
      }
    },
    {
      id: 'create-peer-review-templates',
      name: 'Create Peer Review Templates',
      icon: MessageSquare,
      color: 'task-teal',
      prompts: {
        base: "Create a peer review form for a project on '{topic}' in Science Grade 9.",
        detailed: "Provide a template with specific criteria and questions for students to evaluate each other’s work."
      }
    },
    {
      id: 'generate-answer-keys',
      name: 'Generate Answer Keys',
      icon: CheckCircle,
      color: 'task-blue',
      prompts: {
        base: "Generate model answers for an assignment on '{topic}' in Science Grade 9.",
        detailed: "Provide detailed solutions with explanations for each question in the assignment."
      }
    },
    {
      id: 'auto-grade-submissions',
      name: 'Auto-grade Submissions',
      icon: CheckCircle,
      color: 'task-pink',
      prompts: {
        base: "Auto-grade quiz responses on '{topic}' for Science Grade 9.",
        detailed: "Evaluate objective responses or code submissions, providing scores and feedback based on a provided answer key."
      }
    },
    {
      id: 'plagiarism-check',
      name: 'Plagiarism Check / Rephrase',
      icon: Edit3,
      color: 'task-yellow',
      prompts: {
        base: "Check and rephrase an answer about '{topic}' for Science Grade 9.",
        detailed: "Detect potential duplicate content and provide a rephrased version while maintaining the original meaning."
      }
    },
    {
      id: 'create-viva-questions',
      name: 'Create Viva Questions',
      icon: MessageSquare,
      color: 'task-teal',
      prompts: {
        base: "Create viva questions on '{topic}'.",
        detailed: "Generate oral examination questions with varying difficulty, focusing on key concepts and applications."
      }
    },
    {
      id: 'generate-lesson-plan',
      name: 'Generate Lesson Plan',
      icon: BookOpen,
      color: 'task-blue',
      prompts: {
        base: "Generate a lesson plan on '{topic}'.",
        detailed: "Provide a week-by-week or session-based plan with objectives, activities, assessments, and timelines."
      }
    },
    {
      id: 'design-course-outline',
      name: 'Design Course Outline',
      icon: Folder,
      color: 'task-gray',
      prompts: {
        base: "Design a topic-wise syllabus including '{topic}'.",
        detailed: "Create a course structure with modules, learning objectives, assessments, and a timeline."
      }
    }
  ], []);

  const contentStyle = {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px',
    fontFamily: '"Source Sans Pro", sans-serif',
    fontSize: '14px',
    lineHeight: '1.5',
    textAlign: 'left',
    color: '#111827',
    minHeight: '400px',
  };

  // Generate prompt based on selected tasks and inputs
  const generatedPrompt = useMemo(() => {
    if (selectedTasks.length === 0) {
      return "Please select at least one task category to generate content.";
    }

    const selectedTaskCategories = selectedTasks.map((taskId) =>
      taskCategories.find((cat) => cat.id === taskId)
    );

    let prompt = "";

    selectedTaskCategories.forEach((category, index) => {
      const basePrompt = category.prompts.base
        .replace('{subject}', selectedSubject ? subjects.find(sub => sub.value === selectedSubject)?.label || '[Subject]' : '[Subject]')
        .replace('{topic}', selectedTopic || '[Topic]')
        .replace('{class}', selectedClass ? classes.find(cls => cls.value === selectedClass)?.label || '[Class]' : '[Class]');

      const detailedPrompt = category.prompts.detailed;

      if (index > 0) prompt += "\n\n";
      prompt += `${index + 1}. ${category.name.toUpperCase()}:\n`;
      prompt += `${basePrompt} ${detailedPrompt}`;
    });

    prompt += "\n\nPlease format the response with clear headings, bullet points, and structured sections for easy reading and implementation.";
    
    return prompt;
  }, [selectedTasks, selectedSubject, selectedTopic, selectedClass, subjects, classes, taskCategories]);

  // Fetch chat history
  useEffect(() => {
    const fetchChatHistory = async () => {
      if (!user || !user.userId || authLoading) {
        setError('Waiting for user data to load...');
        return;
      }
      setIsLoadingChatHistory(true);
      try {
        const response = await axios.get(`${configData.AI_CHAT_HISTORY_URL}/${user.userId}/sessions`, {
          headers: {
            'accept': 'application/hal+json',
            'Authorization': `Bearer ${user.token}`,
          }
        });
        setChatHistory(response.data);
      } catch (err) {
        setError(`Error fetching chat history: ${err.response?.status || err.message}`);
      } finally {
        setIsLoadingChatHistory(false);
      }
    };
    fetchChatHistory();
  }, [axios, user, authLoading]);

  // Update customPrompt when inputs change
  useEffect(() => {
    setCustomPrompt(generatedPrompt);
  }, [generatedPrompt]);

  // Sync editedContent with generatedContent when new content is loaded
  useEffect(() => {
    if (generatedContent && isEditing) {
      setEditedContent(cleanMarkdownHeadings(generatedContent));
    }
  }, [generatedContent, isEditing]);

  // Fetch classes from API
  useEffect(() => {
    const fetchClasses = async () => {
      if (!user || !orgId || authLoading) {
        return;
      }
      setIsLoadingClasses(true);
      try {
        const response = await axios.get(`${configData.CLASS_BY_ORG}/${orgId}`);
        const data = response.data;
        const formattedClasses = data.map(cls => {
          const section = (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined') ? String(cls.sectionName).trim() : '';
          return {
            value: cls.classId,
            label: section ? `${cls.className} - ${section}` : `${cls.className || ''}`.trim(),
            courseIds: cls.courseIds ? cls.courseIds.filter(id => id !== 'string') : [],
          };
        });
        setClasses(formattedClasses);
      } catch (err) {
        console.error(`Error fetching classes:`, err);
        setError(`Error fetching classes: ${err.response?.status || err.message}`);
      } finally {
        setIsLoadingClasses(false);
      }
    };
    fetchClasses();
  }, [axios, user, orgId, authLoading]);

  // Fetch subjects (courses) when a class is selected
  useEffect(() => {
    if (!selectedClass || !user || !user.orgId || authLoading) {
      setSubjects([]);
      setTopics([]);
      setSelectedSubject('');
      setSelectedTopic('');
      return;
    }

    const fetchSubjects = async () => {
      setIsLoadingSubjects(true);
      try {
        const selectedClassData = classes.find(cls => cls.value === selectedClass);
        if (!selectedClassData || !selectedClassData.courseIds.length) {
          setSubjects([]);
          setTopics([]);
          setSelectedSubject('');
          setSelectedTopic('');
          return;
        }

        const coursePromises = selectedClassData.courseIds.map(async (courseId) => {
          try {
            const response = await axios.get(`${configData.COURSE_API_URL}/${courseId}`);
            return response;
          } catch (err) {
            if (err.response?.status === 404) {
              console.warn(`Course ID ${courseId} not found, skipping...`);
              return null;
            }
            throw err;
          }
        });

        const courseResponses = await Promise.all(coursePromises);
        const validResponses = courseResponses.filter(response => response !== null);
        if (courseResponses.some(response => response === null)) {
          console.log('Some courses could not be fetched. Displaying available subjects.');
        }

        const formattedSubjects = validResponses
          .filter(response => response.data.courseDetails && response.data.courseDetails.courseName)
          .map(response => ({
            value: response.data.courseId,
            label: response.data.courseDetails.courseName,
            topics: response.data.courseDetails.topics || [],
          }));
        setSubjects(formattedSubjects);
      } catch (err) {
        console.error(`Error fetching subjects: ${err.response?.status || err.message}`);
      } finally {
        setIsLoadingSubjects(false);
      }
    };
    fetchSubjects();
  }, [selectedClass, classes, axios, user, authLoading]);

  // Update topics when a subject is selected
  useEffect(() => {
    if (!selectedSubject) {
      setTopics([]);
      setSelectedTopic('');
      return;
    }

    const selectedSubjectData = subjects.find(sub => sub.value === selectedSubject);
    if (selectedSubjectData && selectedSubjectData.topics) {
      setTopics(selectedSubjectData.topics);
    } else {
      setTopics([]);
    }
  }, [selectedSubject, subjects]);

  // Parse prompt to extract task, class, subject, and topic
  const parsePrompt = (prompt) => {
    const taskMatch = prompt.match(/1\. ([^:]+):/i);
    const taskName = taskMatch ? taskMatch[1].trim().toLowerCase() : '';
    const taskId = taskCategories.find(cat => cat.name.toLowerCase() === taskName.replace(/ & /g, ' and '))?.id || '';

    const classMatch = prompt.match(/for (?:Class|class|Grade|grade) ([^\s]+(?: - [A-B])?)/i);
    const className = classMatch ? classMatch[1] : '';

    const subjectMatch = prompt.match(/for (\w+(?: \w+)?) on/i);
    const subjectName = subjectMatch ? subjectMatch[1] : '';

    const topicMatch = prompt.match(/on '([^']+)'/i);
    const topicName = topicMatch ? topicMatch[1] : '';

    return { taskId, className, subjectName, topicName, prompt };
  };

  // Restore chat session
  const restoreChatSession = (session) => {
    const prompt = session.prompts[0];
    const { taskId, className, subjectName, topicName, prompt: fullPrompt } = parsePrompt(prompt);
    setSelectedTasks(taskId ? [taskId] : []);
    setSelectedClass(classes.find(cls => cls.label.includes(className))?.value || '');
    setSelectedSubject(subjects.find(sub => sub.label.toLowerCase().includes(subjectName.toLowerCase()))?.value || '');
    setSelectedTopic(topics.find(topic => topic.toLowerCase().includes(topicName.toLowerCase())) || '');
    setCustomPrompt(fullPrompt);
    setShowChatHistoryModal(false);
    setIsEditing(true);
    handleGenerateContent();
  };

  // Call AI Conversation API
  const callAIConversation = async (prompt) => {
    if (!user || !user.userId || authLoading) {
      throw new Error('User data not available');
    }
    try {
        const effectiveOrgId = orgId || user.orgId;
        if (!effectiveOrgId) {
            console.error('Assessments: Missing orgId');
            throw new Error('orgId not available');
        }
        const response = await axios.post(
            `${configData.AI_API_URL}?userId=${user.userId}&orgId=${effectiveOrgId}`,
            prompt,
            {
                headers: {
                    'accept': 'application/hal+json',
                }
            }
        );
      return response.data.assistantReply;
    } catch (error) {
      console.error('AI API Error:', error);
      throw new Error(`AI API error: ${error.response?.status || error.message}`);
    }
  };

  // Generate content handler
  const handleGenerateContent = async () => {
    if (selectedTasks.length === 0) {
      toast.error('Please select at least one task category.');
      return;
    }

    if (!selectedClass || !selectedSubject) {
      toast.error('Please select at least Class and Subject.');
      return;
    }

    if (!user || !user.userId || authLoading) {
      toast.error('User data is not loaded. Please try again.');
      return;
    }

    if (!customPrompt.trim()) {
      toast.error('Please provide a prompt in the Custom Prompt field.');
      return;
    }

    setIsGenerating(true);
    try {
      const content = await callAIConversation(customPrompt);
      
      setGeneratedContent(content);
      setEditedContent(cleanMarkdownHeadings(content));
      setIsEditing(true);
      setShowGeneratedContent(true);
      
      const newVersion = {
        version: currentVersion,
        content: content,
        timestamp: new Date().toISOString(),
        settings: {
          class: selectedClass,
          subject: selectedSubject,
          topic: selectedTopic,
          tasks: selectedTasks,
          customPrompt: customPrompt
        }
      };
      
      setContentHistory(prev => [...prev, newVersion]);
      setCurrentVersion(prev => prev + 1);
      setActivityMetrics(prev => ({
        ...prev,
        generated: prev.generated + 1
      }));
      
    } catch (error) {
      toast.error('Error generating content: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Restore content from history
  const restoreVersion = (version) => {
    const historyItem = contentHistory.find(item => item.version === version);
    if (historyItem) {
      const restoredContent = historyItem.content;
      setGeneratedContent(restoredContent);
      setEditedContent(cleanMarkdownHeadings(restoredContent));
      setSelectedClass(historyItem.settings.class || '');
      setSelectedSubject(historyItem.settings.subject || '');
      setSelectedTopic(historyItem.settings.topic || '');
      setSelectedTasks(historyItem.settings.tasks || []);
      setCustomPrompt(historyItem.settings.customPrompt || '');
      setIsEditing(true);
    }
  };

  // Refine content handler
  const handleRefineContent = async () => {
    if (!refinementInstructions.trim()) {
      toast.error('Please provide refinement instructions.');
      return;
    }

    if (!user || !user.userId || authLoading) {
      toast.error('User data is not loaded. Please try again.');
      return;
    }

    setIsGenerating(true);
    try {
      const refinePrompt = `Please refine the following educational content based on these instructions: "${refinementInstructions}"\n\nOriginal Content:\n${generatedContent}`;
      const refinedContent = await callAIConversation(refinePrompt);
      
      setGeneratedContent(refinedContent);
      setEditedContent(cleanMarkdownHeadings(refinedContent));
      setIsEditing(true);
      
      const newVersion = {
        version: currentVersion,
        content: refinedContent,
        timestamp: new Date().toISOString(),
        refinementInstructions: refinementInstructions,
        settings: {
          class: selectedClass,
          subject: selectedSubject,
          topic: selectedTopic,
          tasks: selectedTasks,
          customPrompt: customPrompt
        }
      };
      
      setContentHistory(prev => [...prev, newVersion]);
      setCurrentVersion(prev => prev + 1);
      setActivityMetrics(prev => ({
        ...prev,
        generated: prev.generated + 1
      }));
      setRefinementInstructions('');
      
    } catch (error) {
      toast.error('Error refining content: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Save edited content
  const handleSaveEdits = () => {
    if (!editedContent.trim()) {
      toast.error('Content cannot be empty.');
      return;
    }

    setGeneratedContent(editedContent);
    const newVersion = {
      version: currentVersion,
      content: editedContent,
      timestamp: new Date().toISOString(),
      settings: {
        class: selectedClass,
        subject: selectedSubject,
        topic: selectedTopic,
        tasks: selectedTasks,
        customPrompt: customPrompt
      },
      edited: true
    };
    
    setContentHistory(prev => [...prev, newVersion]);
    setCurrentVersion(prev => prev + 1);
    setActivityMetrics(prev => ({
      ...prev,
      generated: prev.generated + 1
    }));
    setIsEditing(false);
  };

  const publishGeneratedDocx = async ({
  docxBlob,
  fileName,
  classIds,
  courseId = "",
  remarks = "",
  fileCategory = "STUDY_MATERIAL",
  token,
  axios,
  orgId,
}) => {
  if (!docxBlob || classIds.length === 0) {
    toast.error("Missing file or class selection.");
    return;
  }

  try {
    const rawToken = token && token.startsWith("Bearer ") ? token.substring(7) : token;
    const promises = classIds.map(async (targetClassId) => {
      const file = new File([docxBlob], fileName, {
        type:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const record = await uploadFileToCampusFileSystem(file, {
        entityType: "CLASS",
        entityId: targetClassId,
        orgId: orgId || "1",
        category: fileCategory || "STUDY_MATERIAL",
        uploadedBy: "admin",
        token: rawToken,
      });

      try {
        const downloadUrl = record?.downloadUrl;
        const bodyText = `A new assessment document "${fileName}" has been published for your class.\n\n` +
          (remarks ? `Remarks: ${remarks}\n\n` : '') +
          (downloadUrl ? `📎 Document: [${fileName}](${downloadUrl})` : '');

        const authHeader = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
        await axios.post(configData.INBOX_SEND, {
          subject: `📝 New Assessment Document: ${fileName}`,
          body: bodyText,
          orgId: orgId || "1",
          targetType: "CLASS",
          targetId: targetClassId,
          category: "ASSIGNMENT",
          attachmentUrls: downloadUrl ? [downloadUrl] : undefined
        }, {
          headers: { Authorization: authHeader }
        });
      } catch (inboxErr) {
        console.warn("Failed to send inbox notification for assessment document:", inboxErr);
      }

      return record;
    });

    await Promise.all(promises);
    toast.success(
      `Document "${fileName}" published to ${classIds.length} class(es)!`
    );
  } catch (err) {
    console.error("Publish error:", err);
    toast.error(
      err.response?.data?.message || "Failed to publish. Please try again."
    );
  }
};

  // Publish to class API
  const handlePublishToClass = async () => {
    if (!generatedContent) {
      toast.error('No content to publish. Please generate content first.');
      return;
    }

    if (!user || !orgId || authLoading) {
      toast.error('User data is not loaded. Please try again.');
      return;
    }

    if (!selectedClass) {
      toast.error('Please select a class to publish the content.');
      return;
    }

    const subjectLabel = selectedSubject
      ? subjects.find(sub => sub.value === selectedSubject)?.label || 'UnknownSubject'
      : 'UnknownSubject';
    const topicLabel = selectedTopic || 'GeneralTopic';
    const safeTopic = topicLabel.replace(/[^a-zA-Z0-9]/g, '_');
    const defaultFileName = `${safeTopic}_AI.docx`;

    setFileNameInput(defaultFileName);
    setShowFileNameModal(true);
  };

  // Handle file name submission
 const handleFileNameSubmit = async () => {
  if (!fileNameInput.trim()) {
    toast.error("File name cannot be empty.");
    return;
  }

  let fileName = fileNameInput.trim();
  if (!fileName.toLowerCase().endsWith(".docx")) fileName += ".docx";
  fileName = fileName.replace(/[^a-zA-Z0-9-_ ]/g, "").replace(/\s+/g, "_");
  if (fileName === ".docx") {
    toast.error("Invalid file name.");
    return;
  }

  setIsPublishing(true);
  try {
    const cleanedContent = cleanMarkdownHeadings(generatedContent);
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            new Paragraph({ children: [new TextRun({ text: "Generated Educational Content", bold: true, size: 24 })] }),
            new Paragraph({ children: [new TextRun({ text: `Class: ${classes.find(c => c.value === selectedClass)?.label || "N/A"}`, size: 20 })] }),
            new Paragraph({ children: [new TextRun({ text: `Subject: ${subjects.find(s => s.value === selectedSubject)?.label || "N/A"}`, size: 20 })] }),
            new Paragraph({ children: [new TextRun({ text: `Topic: ${selectedTopic || "N/A"}`, size: 20 })] }),
            new Paragraph({ children: [new TextRun({ text: `Task: ${selectedTasks.map(t => taskCategories.find(c => c.id === t)?.name).join(", ")}`, size: 20 })] }),
            new Paragraph({ children: [new TextRun({ text: `Generated On: ${new Date().toLocaleDateString()}`, size: 20 })] }),
            new Paragraph({ children: [new TextRun({ text: cleanedContent, size: 20 })] }),
          ],
        },
      ],
    });

    const docxBlob = await Packer.toBlob(doc);

    const classIds = Array.isArray(selectedClass)
      ? selectedClass.map(c => (typeof c === "string" ? c : c.value))
      : [selectedClass];

    // CALL THE HELPER
    await publishGeneratedDocx({
      docxBlob,
      fileName,
      classIds,
      courseId: selectedSubject,
      remarks: publishRemarks.trim(),
      fileCategory: "STUDY_MATERIAL",
      token: user.token,
      axios,
      orgId: orgId,
    });

    setActivityMetrics(prev => ({ ...prev, published: prev.published + 1 }));
    setShowFileNameModal(false);
    setFileNameInput("");
    setPublishRemarks("");   // ← reset remarks
  } catch (err) {
    console.error("Publish error:", err);
    toast.error("Failed to publish.");
  } finally {
    setIsPublishing(false);
  }
};

  // Download generated content as DOCX or PDF
  const handleDownload = async (format) => {
    if (!generatedContent) {
      toast.error('No content to download. Please generate content first.');
      return;
    }

    try {
      const cleanedContent = cleanMarkdownHeadings(generatedContent);
      
      if (format === 'docx') {
        const doc = new Document({
          sections: [
            {
              properties: {},
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'Generated Educational Content',
                      bold: true,
                      size: 24,
                    }),
                  ],
                  spacing: { after: 200 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Class: ${selectedClass ? classes.find(cls => cls.value === selectedClass)?.label || 'N/A' : 'N/A'}`,
                      size: 20,
                    }),
                  ],
                  spacing: { after: 100 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Subject: ${selectedSubject ? subjects.find(sub => sub.value === selectedSubject)?.label || 'N/A' : 'N/A'}`,
                      size: 20,
                    }),
                  ],
                  spacing: { after: 100 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Topic: ${selectedTopic || 'N/A'}`,
                      size: 20,
                    }),
                  ],
                  spacing: { after: 100 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Task Categories: ${selectedTasks.map(taskId => taskCategories.find(cat => cat.id === taskId)?.name || '').join(', ') || 'N/A'}`,
                      size: 20,
                    }),
                  ],
                  spacing: { after: 100 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Generated On: ${new Date().toLocaleDateString()}`,
                      size: 20,
                    }),
                  ],
                  spacing: { after: 200 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: cleanedContent,
                      size: 20,
                    }),
                  ],
                }),
              ],
            },
          ],
        });

        const blob = await Packer.toBlob(doc);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `generated-content-${currentVersion}.docx`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else if (format === 'pdf') {
        if (!window.jspdf || !window.jspdf.jsPDF) {
          toast.error('jsPDF library is not loaded. Please check your network connection.');
          return;
        }

        if (isHtml2CanvasLoaded) {
          await generatePDFFromHTML(cleanedContent);
        } else {
          if (html2CanvasLoadError) {
            toast.warn(html2CanvasLoadError);
          }
          generateBasicPDF(cleanedContent);
        }
      }
    } catch (error) {
      console.error(`Error generating ${format.toUpperCase()}:`, error);
      toast.error(`Error generating ${format.toUpperCase()}: ${error.message}`);
    }
  };

  const generatePDFFromHTML = async (content) => {
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '800px';
    tempDiv.style.padding = '20px';
    tempDiv.style.fontFamily = '"Source Sans Pro", sans-serif';
    tempDiv.style.fontSize = '14px';
    tempDiv.style.lineHeight = '1.6';
    tempDiv.style.backgroundColor = 'white';
    
    tempDiv.innerHTML = `
      <h1 style="font-size: 18px; font-weight: bold; margin-bottom: 20px;">Generated Educational Content</h1>
      <p><strong>Class:</strong> ${selectedClass ? classes.find(cls => cls.value === selectedClass)?.label || 'N/A' : 'N/A'}</p>
      <p><strong>Subject:</strong> ${selectedSubject ? subjects.find(sub => sub.value === selectedSubject)?.label || 'N/A' : 'N/A'}</p>
      <p><strong>Topic:</strong> ${selectedTopic || 'N/A'}</p>
      <p><strong>Task Categories:</strong> ${selectedTasks.map(taskId => taskCategories.find(cat => cat.id === taskId)?.name || '').join(', ') || 'N/A'}</p>
      <p><strong>Generated On:</strong> ${new Date().toLocaleDateString()}</p>
      <hr style="margin: 20px 0;">
      <div style="white-space: pre-wrap;">${DOMPurify.sanitize(renderMarkdown(content)).replace(/\n/g, '<br>')}</div>
    `;
    
    document.body.appendChild(tempDiv);

    try {
      const canvas = await window.html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF();
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`generated-content-${currentVersion}.pdf`);
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  const generateBasicPDF = (content) => {
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();
    doc.setFont('helvetica');
    doc.setFontSize(12);
    
    const metadata = [
      'Generated Educational Content',
      `Class: ${selectedClass ? classes.find(cls => cls.value === selectedClass)?.label || 'N/A' : 'N/A'}`,
      `Subject: ${selectedSubject ? subjects.find(sub => sub.value === selectedSubject)?.label || 'N/A' : 'N/A'}`,
      `Topic: ${selectedTopic || 'N/A'}`,
      `Task Categories: ${selectedTasks.map(taskId => taskCategories.find(cat => cat.id === taskId)?.name || '').join(', ') || 'N/A'}`,
      `Generated On: ${new Date().toLocaleDateString()}`,
      '',
      content
    ].join('\n');

    const processedContent = metadata
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .replace(/#{1,6}\s*(.*)/g, '$1')
      .replace(/π/g, 'π')
      .replace(/∞/g, '∞')
      .replace(/≤/g, '≤')
      .replace(/≥/g, '≥')
      .replace(/√/g, '√')
      .replace(/²/g, '²')
      .replace(/³/g, '³');
    
    const lines = doc.splitTextToSize(processedContent, 180);
    let yPosition = 20;
    
    lines.forEach(line => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(line, 15, yPosition);
      yPosition += 7;
    });

    doc.save(`generated-content-${currentVersion}.pdf`);
  };

  // Reset to main view
  const handleBack = () => {
    setShowGeneratedContent(false);
    setGeneratedContent('');
    setEditedContent('');
    setIsEditing(false);
    setSelectedTasks([]);
    setSelectedClass('');
    setSelectedSubject('');
    setSelectedTopic('');
    setCustomPrompt('');
    setRefinementInstructions('');
    setSuggestions('');
    setShowSuggestions(false);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const Dropdown = ({ value, onChange, placeholder, options = [], isLoading = false }) => (
    <div className="dropdown-container">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="dropdown-select"
        disabled={isLoading}
      >
        <option value="">{isLoading ? 'Loading...' : placeholder}</option>
        {options.map((option, index) => (
          <option key={index} value={typeof option === 'string' ? option : option.value}>
            {typeof option === 'string' ? option : option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="dropdown-icon" />
    </div>
  );

  // Show generated content view
  if (showGeneratedContent) {
    return (
      <div className="faculty-assistant">
        <div style={{ display: 'flex', minHeight: '100vh' }}>
          {/* Left Sidebar for Content History */}
          {contentHistory.length > 0 && (
            <div className="content-history-sidebar">
              <div className="content-history-header">
                <button
                  onClick={handleBack}
                  className="content-back-btn"
                  title="Back to main view"
                >
                  <ArrowLeft size={20} />
                </button>
                <h3 className="content-history-title">Content History</h3>
              </div>
        
              {contentHistory.map((item, index) => {
                const getSummaryName = () => {
                  const task = item.settings.tasks.length > 0 
                    ? taskCategories.find(cat => cat.id === item.settings.tasks[0])?.name || 'Generated Content'
                    : 'Generated Content';
                  const topic = item.settings.topic || '';
                  const subject = item.settings.subject 
                    ? subjects.find(sub => sub.value === item.settings.subject)?.label || ''
                    : '';
                  
                  if (topic) {
                    const words = topic.split(/\s+/).slice(0, 3).join(' ');
                    return words.split(/\s+/).length > 1 ? words : `${task.split(/\s+/)[0]} ${topic}`;
                  }
                  if (subject) {
                    const words = subject.split(/\s+/).slice(0, 3).join(' ');
                    return words.split(/\s+/).length > 1 ? words : `${task.split(/\s+/)[0]} ${subject}`;
                  }
                  return task.split(/\s+/).slice(0, 2).join(' ');
                };

                return (
                  <div key={index} className="history-item">
                    <div className="history-item-content">
                      <div>
                        <strong className="history-item-version">{getSummaryName()}</strong>
                        {item.refinementInstructions && (
                          <span className="history-item-meta">{item.refinementInstructions}</span>
                        )}
                        {item.edited && (
                          <span className="history-item-meta">Manually Edited</span>
                        )}
                        {item.shareUrl && (
                          <span className="history-item-meta">
                            Shared: {new Date(item.sharedTimestamp).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => restoreVersion(item.version)}
                        className="history-item-restore"
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Main Content Area */}
          <div className="content-area">
            <div className="headerandpublish">
              <h3>Generated Content</h3>
              <button
                onClick={handlePublishToClass}
                disabled={isPublishing}
                className="content-publish-btn"
              >
                {isPublishing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                {isPublishing ? 'Publishing...' : 'Publish to Class'}
              </button>
            </div>

            {/* Display jsPDF or html2canvas load error if any */}
            {(jsPDFLoadError || html2CanvasLoadError) && (
              <div style={{ color: 'red', marginBottom: '16px', fontSize: '14px' }}>
                {jsPDFLoadError || html2CanvasLoadError}
              </div>
            )}

            {/* Editable Content Area */}
            <div className="content-display">
              <div className="content-buttons-top">
                <button
                  onClick={() => handleDownload('docx')}
                  disabled={isGenerating || !generatedContent}
                  className="content-download-btn icon-only"
                  title="Download as DOCX"
                >
                  <Download size={16} />
                  <span className="download-label">DOCX</span>
                </button>
                <button
                  onClick={() => handleDownload('pdf')}
                  disabled={isGenerating || !generatedContent || !isJsPDFLoaded}
                  className="content-download-btn icon-only"
                  title={isJsPDFLoaded ? "Download as PDF" : "Loading jsPDF..."}
                >
                  <Download size={16} />
                  <span className="download-label">{isJsPDFLoaded ? "PDF" : "Loading..."}</span>
                </button>
              </div>
              {isEditing ? (
                <>
                  <ReactQuill
                    value={editedContent}
                    onChange={setEditedContent}
                    theme="snow"
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['link', 'image'],
                        ['clean']
                      ]
                    }}
                    style={{ minHeight: '300px', marginBottom: '16px' }}
                  />
                  <div className="button-group">
                    <button
                      onClick={handleSaveEdits}
                      className="content-save-btn"
                    >
                      <CheckCircle size={16} />
                      Save Changes
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="content-preview-btn"
                    >
                      Preview
                    </button>
                  </div>
                </>
              ) : (
                <div
                  className="markdown-content"
                  style={contentStyle}
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(generatedContent) }}
                />
              )}
            </div>

            {/* File Name Modal */}
            {/* File Name + Remarks Modal */}
{showFileNameModal && (
  <div className="file-name-modal-overlay">
    <div className="file-name-modal">
      <h3 className="file-name-modal-title">Publish to Class</h3>

      {/* File Name */}
      <label className="form-label">
        File Name <span className="required">*</span>
      </label>
      <input
        type="text"
        value={fileNameInput}
        onChange={(e) => setFileNameInput(e.target.value)}
        placeholder="e.g. Photosynthesis_Assignment_AI.docx"
        className="file-name-input"
        autoFocus
      />

      {/* Remarks */}
      <label className="form-label" style={{ marginTop: '16px' }}>
        Remarks (optional)
      </label>
      <textarea
        value={publishRemarks}
        onChange={(e) => setPublishRemarks(e.target.value)}
        placeholder="e.g. AI-generated assignment for Grade 8 Biology, includes MCQs and diagrams"
        className="remarks-textarea"
        rows={3}
      />

      <div className="button-group" style={{ marginTop: '20px' }}>
        <button
          onClick={handleFileNameSubmit}
          className="file-name-submit-button"
          disabled={isPublishing}
        >
          {isPublishing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
          {isPublishing ? 'Publishing...' : 'Publish'}
        </button>
        <button
          onClick={() => {
            setShowFileNameModal(false);
            setFileNameInput('');
            setPublishRemarks('');   // ← reset remarks
          }}
          className="content-cancel-btn"
          disabled={isPublishing}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

            <div className="refine-section">
              <h3 className="refine-title">Refine Content</h3>
              <p className="refine-description">
                Modify the response by providing additional instructions or asking for specific changes.
              </p>
              <textarea
                value={refinementInstructions}
                onChange={(e) => setRefinementInstructions(e.target.value)}
                placeholder="e.g., 'Convert to table format', 'Add Python coding examples', 'Include assessment rubrics', 'Make it more engaging for students'"
                className="refine-textarea"
              />
              <div className="refine-buttons">
                <button
                  onClick={handleRefineContent}
                  disabled={isGenerating}
                  className="content-refine-btn"
                >
                  {isGenerating ? <Loader2 size={16} className="animate-spin" /> : null}
                  Refine Content
                </button>
              </div>
            </div>

            <div className="button-group">
              <button
                onClick={handlePublishToClass}
                disabled={isPublishing}
                className="content-publish-btn"
              >
                {isPublishing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                {isPublishing ? 'Publishing...' : 'Publish to Class'}
              </button>
            </div>
          </div>
        </div>

      </div>
    );
  }

  // Main form view
  return (
    <div className="faculty-assistant">
      <div className="main-container">
        <div className="section-header">
          <h2 className="section-title">Faculty Assistant</h2>
          <button
            onClick={() => setShowChatHistoryModal(true)}
            className="history-btn"
            title="View Chat History"
          >
            <Clock size={20} />
            History
          </button>
        </div>

        {/* Filter Dropdowns */}
        <div className="filter-grid">
          <div className="filter-item">
            <label className="filter-label">Class</label>
            <Dropdown 
              value={selectedClass}
              onChange={setSelectedClass}
              placeholder="Select Class..."
              options={classes}
              isLoading={isLoadingClasses || authLoading}
            />
          </div>
          <div className="filter-item">
            <label className="filter-label">Subject</label>
            <Dropdown 
              value={selectedSubject}
              onChange={setSelectedSubject}
              placeholder="Select Subject..."
              options={subjects}
              isLoading={isLoadingSubjects || authLoading}
            />
          </div>
          <div className="filter-item">
            <label className="filter-label">Topic</label>
            <Dropdown 
              value={selectedTopic}
              onChange={setSelectedTopic}
              placeholder="Select Topic..."
              options={topics}
              isLoading={isLoadingTopics || authLoading}
            />
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{ color: 'red', marginBottom: '16px', fontSize: '14px' }}>
            {error}
          </div>
        )}
        
        {/* Scrollable Task Categories Container */}
        <div className="scrollable-tasks-container">
          <div className="task-categories">
            {taskCategories.map((category) => {
              const Icon = category.icon;
              const isSelected = selectedTasks.includes(category.id);
              
              return (
                <div
                  key={category.id}
                  className={`task-category ${category.color} ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleTaskSelection(category.id)}
                >
                  <div className="task-content">
                    <div className="task-left">
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleTaskSelection(category.id);
                        }}
                        className="task-checkbox"
                      />
                      <Icon className="task-icon" />
                      <span className="task-name">{category.name}</span>
                    </div>
                    <div className="question-container">
                      <HelpCircle className="question-icon" />
                      <div className="task-tooltip">
                        {category.prompts.detailed}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sticky Highlighted Custom Prompt */}
        <div className="sticky-custom-prompt">
          <div className="custom-prompt">
            <div className="prompt-header">
              <h3 className="prompt-title">Custom Prompt</h3>
              <Edit3 className="edit-icon" />
            </div>
            <textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Select tasks and inputs to generate a prompt, then edit or add details here..."
              className="prompt-textarea"
              rows={6}
            />
            <button 
              className="generate-btn"
              onClick={handleGenerateContent}
              disabled={isGenerating || authLoading || !customPrompt.trim()}
            >
              {isGenerating ? (
                <>
                  <Loader2 
                    className="sparkles-icon" 
                    style={{ animation: 'spin 1s linear infinite' }}
                  />
                  <span>Generating Content Please Wait...</span>
                </>
              ) : (
                <>
                  <Sparkles className="sparkles-icon" />
                  <span>Generate Content</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Chat History Modal */}
        {showChatHistoryModal && (
          <div className="file-name-modal-overlay">
            <div className="file-name-modal">
              <h3 className="file-name-modal-title">Chat History</h3>
              {isLoadingChatHistory ? (
                <p className="chat-history-loading">Loading chat history...</p>
              ) : chatHistory.length === 0 ? (
                <p className="chat-history-empty">No chat history available</p>
              ) : (
                <div className="chat-history-list">
                  {chatHistory.map((session, index) => {
                    const { taskId, className, subjectName, topicName } = parsePrompt(session.prompts[0]);
                    const task = taskCategories.find(cat => cat.id === taskId)?.name || 'Unknown Task';
                    const summary = `${task} - ${subjectName || 'N/A'} - ${topicName || 'N/A'}`;
                    
                    return (
                      <div key={session.sessionId} className="chat-history-item">
                        <div className="chat-history-content">
                          <div>
                            <strong className="chat-history-version">{summary}</strong>
                          </div>
                          <button
                            onClick={() => restoreChatSession(session)}
                            className="chat-history-restore"
                          >
                            Restore
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="button-group">
                <button
                  onClick={() => setShowChatHistoryModal(false)}
                  className="content-cancel-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
